import React, { FC, memo, useEffect } from 'react';
import {
  useNotificationsActions,
  INotification,
  NotificationType,
} from '@src/shared/context/notifications';
import iconSuccess from '@assets/JETSuccess-icon-support-positive.svg';
import iconInfo from '@assets/JETInfo-icon-support-info.svg';
import iconError from '@assets/JETError-icon-support-error.svg';
import iconWarning from '@assets/JETWarning-icon-support-warning.svg';
import useStyles from './styles';
import JetFontIcon, { JET_FONTICON_ALIASES } from '../JetFontIcon';
import { format } from 'date-fns';
import { Typography } from '@material-ui/core';
import { trackNotificationCloseEvent } from '@src/shared/tracking/notification';
import JetButton from '../JetButton';

interface INotificationActionButton {
  text: string;
  onClick: () => void;
}

interface INotificationProps {
  notification: INotification;
  displayDuration: number;
  customStyle?: object;
  showNotificationTime?: boolean;
  showCloseButton?: boolean;
  confirmationButton?: INotificationActionButton;
  cancellationButton?: INotificationActionButton;
  fullsize?: boolean;
}

const Notification: FC<INotificationProps> = ({
  notification,
  displayDuration,
  customStyle,
  showNotificationTime = false,
  showCloseButton = true,
  fullsize = false,
  confirmationButton,
  cancellationButton,
}) => {
  const classes = useStyles();
  const { hideNotification } = useNotificationsActions();
  const iconMapping = {
    [NotificationType.Success]: iconSuccess,
    [NotificationType.Information]: iconInfo,
    [NotificationType.Error]: iconError,
    [NotificationType.Warning]: iconWarning,
  } as any;
  useEffect(() => {
    if (displayDuration > 0) {
      setTimeout(() => {
        hideNotification(notification.id);
      }, displayDuration);
    }
  }, []);

  return (
    <li
      className={`jetNotification ${classes.notification} type-${
        notification.type
      } ${fullsize ? 'fullsize' : ''}`}
      style={customStyle}
    >
      <div
        className={`${classes.notificationInnerWrapper} ${
          !notification.title ? 'no-title' : ''
        }`}
      >
        <div className={classes.notificationIcon}>
          <img
            src={iconMapping[notification.type]}
            className={classes.notificationIcon}
            alt={notification.type}
            width="20"
            height="20"
          />
        </div>
        <div className={classes.notificationMessageWrapper}>
          {notification.title ? (
            <div className={classes.notificationHeader}>
              {notification.title ? (
                <div className={classes.notificationTitle}>
                  <Typography variant="body1" className="body_bold">
                    {notification.title}
                  </Typography>{' '}
                </div>
              ) : null}
              {showNotificationTime ? (
                <div>
                  <Typography
                    variant="body1"
                    className="body_regular"
                  >{`${format(notification.timestamp, 'HH:mm')}`}</Typography>
                </div>
              ) : null}
              {showCloseButton ? (
                <JetFontIcon
                  className={classes.closeIcon}
                  onClick={() => {
                    trackNotificationCloseEvent(notification.message);
                    hideNotification(notification.id);
                  }}
                  iconname={JET_FONTICON_ALIASES.CLOSE}
                />
              ) : null}
            </div>
          ) : null}
          <div className={classes.notificationContent}>
            <Typography variant="body1" className="body_regular">
              {notification.message}
            </Typography>
          </div>
        </div>
      </div>
      {cancellationButton || confirmationButton ? (
        <div className={classes.actionButtons}>
          {cancellationButton && (
            <JetButton
              onClick={cancellationButton.onClick}
              variation={'ghost'}
              size={'xsmall'}
            >
              {cancellationButton.text}
            </JetButton>
          )}
          {confirmationButton && (
            <JetButton
              onClick={confirmationButton.onClick}
              variation={'primary_dark'}
              size={'xsmall'}
            >
              {confirmationButton.text}
            </JetButton>
          )}
        </div>
      ) : null}
    </li>
  );
};

export default memo(Notification);
